import { Component, Input, OnChanges } from '@angular/core';
import { Router } from '@angular/router';

import { resourceTypeLabel } from '../../../core/utils/resource-type-label.util';
import { ReviewStatsErrorBucket } from '../../interfaces/review-stats.interface';

type TagType = 'danger' | 'warning' | 'neutral';

interface ErrorCard {
  title: string;
  /** O que fazer — a retentativa resolve sozinha ou alguém precisa agir. */
  hint: string;
  subtitle: string;
  countLabel: string;
  countType: TagType;
  retryingLabel: string;
  stalledLabel: string;
  deadLabel: string;
  cause: string;
  message: string;
  reviews: { label: string; resourceType: string; resourceId: string }[];
}

/** O que cada tipo de falha significa para quem opera, e o que fazer. */
const KINDS: Record<string, { title: (status?: number) => string; hint: string }> = {
  timeout: {
    title: () => 'O DTA não respondeu a tempo',
    hint: 'A retentativa costuma resolver. Se repetir, verifique a latência do DTA ou o tamanho do prompt.'
  },
  canceled: {
    title: () => 'Envio ao DTA interrompido',
    hint: 'Em geral o worker foi reiniciado ou encerrado durante o envio. Reprocesse o review.'
  },
  rejected: {
    title: status => `O DTA recusou o pedido${status ? ` (HTTP ${status})` : ''}`,
    hint: 'Retentar não resolve: a causa está na configuração do agente no DTA ou no conteúdo enviado.'
  },
  unavailable: {
    title: status => `DTA indisponível${status ? ` (HTTP ${status})` : ''}`,
    hint: 'Falha de rede ou do serviço. A retentativa costuma resolver quando ele volta.'
  },
  invalid_answer: {
    title: () => 'Resposta do assistente fora do formato',
    hint: 'O modelo devolveu algo diferente do JSON esperado. A retentativa costuma resolver; se persistir, revise o prompt.'
  },
  other: {
    title: () => 'Falha no processamento',
    hint: ''
  }
};

const STAGES: Record<string, string> = {
  assistant: 'Análise do assistente',
  review: 'Preparo do review'
};

/**
 * Causas de falha da fila, uma por cartão: o que aconteceu em linguagem de operação, a
 * causa extraída da mensagem, o que fazer e os reviews afetados. A mensagem técnica fica
 * recolhida — é o que se copia para um chamado, não o que se lê primeiro.
 */
@Component({
  selector: 'app-review-stats-health-errors',
  templateUrl: './review-stats-health-errors.component.html',
  styleUrls: ['./review-stats-health-errors.component.css']
})
export class ReviewStatsHealthErrorsComponent implements OnChanges {
  @Input() errors: ReviewStatsErrorBucket[] = [];
  /** Dentro de um item de accordion o título já vem no cabeçalho dele. */
  @Input() showHeading = true;

  cards: ErrorCard[] = [];
  /** Índices dos cartões com a mensagem técnica aberta. */
  expanded = new Set<number>();

  constructor(private router: Router) {}

  ngOnChanges(): void {
    this.cards = (this.errors ?? []).map(error => this.toCard(error));
    this.expanded.clear();
  }

  toggleMessage(index: number): void {
    if (this.expanded.has(index)) {
      this.expanded.delete(index);
    } else {
      this.expanded.add(index);
    }
  }

  openReview(review: { resourceType: string; resourceId: string }): void {
    this.router.navigate(['/menu/smart-review/reviews', review.resourceType, review.resourceId]);
  }

  private toCard(error: ReviewStatsErrorBucket): ErrorCard {
    const kind = KINDS[error.kind] ?? KINDS['other'];
    const [jobType, promptType] = (error.stage ?? '').split('/');
    const stage = [STAGES[jobType] ?? jobType, promptType].filter(Boolean).join(' · ');
    const lastSeen = formatDateTime(error.lastSeen);
    return {
      title: kind.title(error.httpStatus),
      hint: kind.hint,
      subtitle: [stage, lastSeen ? `última em ${lastSeen}` : ''].filter(Boolean).join(' · '),
      countLabel: error.count === 1 ? '1 falha' : `${error.count.toLocaleString('pt-BR')} falhas`,
      countType: error.dead > 0 ? 'danger' : 'warning',
      retryingLabel: error.retrying ? `${error.retrying} em retentativa` : '',
      stalledLabel: error.stalled ? `${error.stalled} parado${error.stalled === 1 ? '' : 's'}` : '',
      deadLabel: error.dead ? `${error.dead} na DLQ` : '',
      cause: error.cause ?? '',
      message: error.message,
      reviews: (error.reviews ?? []).map(r => ({
        label: `${resourceTypeLabel(r.resourceType) || r.resourceType} ${r.resourceId}`,
        resourceType: r.resourceType,
        resourceId: r.resourceId
      }))
    };
  }
}

/** RFC 3339 em UTC → data e hora locais, `dd/mm/aaaa hh:mm`. */
function formatDateTime(raw: string): string {
  const date = new Date(raw);
  if (!raw || Number.isNaN(date.getTime())) { return ''; }
  return date.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}
